<script lang="ts">
    import { fade, scale } from 'svelte/transition';
    import { useForm } from '@inertiajs/svelte';

    interface Props {
        question: any;
        answerUrl: string;
        onclose: () => void;
    }

    let { question, answerUrl, onclose }: Props = $props();

    const form = useForm({
        question_id: question.id,
        response_text: '',
        selected_option_id: null as number | null,
    });

    function submit(e: SubmitEvent) {
        e.preventDefault();
        $form.post(answerUrl, {
            onSuccess: () => onclose(),
        });
    }

    function handleBackdropClick(e: MouseEvent) {
        if (e.target === e.currentTarget) {
            onclose();
        }
    }

    function handleKeydown(e: KeyboardEvent) {
        if (e.key === 'Escape') {
            onclose();
        }
    }
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
<div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    onclick={handleBackdropClick}
    transition:fade={{ duration: 150 }}
>
    <div class="bg-gray-900 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6" transition:scale={{ duration: 200, start: 0.95 }}>
        <div class="flex justify-between items-start mb-4">
            <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
            <h3 class="font-semibold text-gray-200 prose prose-sm prose-invert max-w-none">{@html question.body}</h3>
            <button onclick={onclose} class="text-gray-500 hover:text-gray-300 text-xl leading-none shrink-0 ml-4">&times;</button>
        </div>

        <form onsubmit={submit} class="space-y-3">
            {#if question.type === 'multiple_choice'}
                <div class="grid gap-2">
                    {#each question.options as option, i (option.id)}
                        {@const selected = $form.selected_option_id === option.id}
                        <button
                            type="button"
                            onclick={() => $form.selected_option_id = option.id}
                            class="flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 text-left text-sm transition-colors
                                {selected
                                    ? 'border-cyan-500 bg-cyan-950 ring-1 ring-cyan-500'
                                    : 'border-gray-700 bg-gray-800 hover:border-gray-600'}"
                        >
                            <span class="shrink-0 size-5 rounded-full border-2 flex items-center justify-center
                                {selected ? 'border-cyan-500 bg-cyan-500' : 'border-gray-600 bg-gray-800'}">
                                {#if selected}
                                    <span class="size-2 rounded-full bg-white"></span>
                                {/if}
                            </span>
                            <span class="truncate {selected ? 'font-medium text-cyan-200' : 'text-gray-300'}">
                                {option.body}
                            </span>
                        </button>
                    {/each}
                </div>
                {#if $form.errors.selected_option_id}
                    <p class="text-red-500 text-sm">{$form.errors.selected_option_id}</p>
                {/if}
            {:else}
                <textarea
                    bind:value={$form.response_text}
                    rows={4}
                    class="w-full border border-gray-700 bg-gray-800 text-gray-100 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 {$form.errors.response_text ? 'border-red-500' : ''}"
                    placeholder="Type your answer..."
                ></textarea>
                {#if $form.errors.response_text}
                    <p class="text-red-500 text-sm">{$form.errors.response_text}</p>
                {/if}
            {/if}

            {#if $form.errors.case}
                <p class="text-red-500 text-sm">{$form.errors.case}</p>
            {/if}
            {#if $form.errors.question_id}
                <p class="text-red-500 text-sm">{$form.errors.question_id}</p>
            {/if}

            <div class="flex justify-end gap-3 pt-2">
                <button
                    type="button"
                    onclick={onclose}
                    class="px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={$form.processing}
                    class="bg-cyan-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 transition-colors"
                >
                    {$form.processing ? 'Submitting...' : 'Submit Answer'}
                </button>
            </div>
        </form>
    </div>
</div>
